import Modal from './Modal';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success' | 'primary';
  loading?: boolean;
  error?: string;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'warning',
  loading = false,
  error,
}: ConfirmDialogProps) {
  const variantStyles = {
    danger: {
      bgIcon: 'bg-rose-50 text-rose-600',
      icon: XCircleIcon,
      button: 'bg-rose-600 hover:bg-rose-700 text-white shadow-sm',
    },
    warning: {
      bgIcon: 'bg-amber-50 text-amber-600',
      icon: ExclamationTriangleIcon,
      button: 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm',
    },
    info: {
      bgIcon: 'bg-blue-50 text-blue-600',
      icon: InformationCircleIcon,
      button: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
    },
    success: {
      bgIcon: 'bg-emerald-50 text-emerald-600',
      icon: CheckCircleIcon,
      button: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
    },
    primary: {
      bgIcon: 'bg-emerald-50 text-emerald-600',
      icon: InformationCircleIcon,
      button: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
    },
  };

  const style = variantStyles[variant] || variantStyles.warning;
  const IconComponent = style.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 transition-colors"
          >
            {cancelText}
          </button>
          {!error && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed ${style.button}`}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processando...
                </span>
              ) : (
                confirmText
              )}
            </button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3.5">
          <div className={`p-2.5 rounded-full flex-shrink-0 ${style.bgIcon}`}>
            <IconComponent className="w-6 h-6" />
          </div>
          <div className="flex-1 pt-0.5">
            <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-3.5 border border-red-200">
            <div className="flex">
              <div className="ml-1">
                <h3 className="text-sm font-semibold text-red-800">Erro inesperado</h3>
                <div className="mt-1 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

